/**
 * PlaylistImporter - Importa playlists completas de YouTube/Spotify a un evento.
 * 1) Pega URL → preview de tracks (sin descargar nada)
 * 2) Selecciona los que quieras → import (descarga MP3 en lote)
 * Spotify se resuelve automáticamente a YouTube vía búsqueda yt-dlp.
 */
import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ListMusic, Search, Download, RefreshCw, CheckSquare, Square, Music2 } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config';

const PlaylistImporter = ({ playlistId, onImportSuccess, onClose }) => {
  const [url, setUrl] = useState('');
  const [nota, setNota] = useState('');
  const [autoDownload, setAutoDownload] = useState(true);
  const [autoResolveSpotify, setAutoResolveSpotify] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [importing, setImporting] = useState(false);
  const [tracks, setTracks] = useState([]);
  const [source, setSource] = useState(null); // 'youtube' | 'spotify'
  const [selected, setSelected] = useState(new Set());

  const allSelected = useMemo(
    () => tracks.length > 0 && selected.size === tracks.length,
    [tracks, selected]
  );

  const detectKind = (raw) => {
    const u = (raw || '').trim().toLowerCase();
    if (u.includes('open.spotify.com/') && u.includes('/playlist/')) return 'spotify_playlist';
    if (u.includes('youtube.com') || u.includes('youtu.be')) return 'youtube';
    return null;
  };

  const handlePreview = async () => {
    if (!url.trim()) {
      toast.error('Pega una URL de playlist YouTube o Spotify');
      return;
    }
    setLoadingPreview(true);
    setTracks([]);
    setSelected(new Set());
    setSource(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/peticiones/playlist-preview`,
        { url: url.trim(), max_items: 100 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const list = res.data.tracks || [];
      setTracks(list);
      setSource(res.data.source);
      // Por defecto seleccionar todo
      setSelected(new Set(list.map((_, i) => i)));
      toast.success(`${list.length} canciones detectadas (${res.data.source})`);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(`No se pudo cargar la playlist: ${detail}`);
    } finally {
      setLoadingPreview(false);
    }
  };

  const toggle = (idx) => {
    const next = new Set(selected);
    if (next.has(idx)) next.delete(idx);
    else next.add(idx);
    setSelected(next);
  };

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(tracks.map((_, i) => i)));
  };

  const handleImport = async () => {
    const items = tracks.filter((_, i) => selected.has(i));
    if (items.length === 0) {
      toast.error('Selecciona al menos una canción');
      return;
    }
    if (!playlistId) {
      toast.error('Falta el ID del evento');
      return;
    }
    setImporting(true);
    const toastId = toast.loading(
      `⏬ Importando ${items.length} canciones${autoDownload ? ' (descargando MP3, puede tardar)' : ''}…`,
      { duration: Infinity }
    );
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/peticiones/playlist-import`,
        {
          playlist_id: playlistId,
          nota: nota,
          tracks: items,
          auto_download_mp3: autoDownload,
          auto_resolve_spotify: autoResolveSpotify,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 1000 * 60 * 30, // 30 min cap
        }
      );
      const s = res.data.summary || {};
      toast.success(
        `✅ ${s.imported || 0} importadas · ${s.downloaded || 0} con MP3 · ${s.failed_download || 0} sin audio · ${s.failed_resolve || 0} sin equivalente`,
        { id: toastId, duration: 8000 }
      );
      if (typeof onImportSuccess === 'function') onImportSuccess(res.data);
      // Reset
      setTracks([]);
      setSelected(new Set());
      setUrl('');
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(`Error importando: ${detail}`, { id: toastId });
    } finally {
      setImporting(false);
    }
  };

  const detectedKind = detectKind(url);

  return (
    <div className="space-y-4" data-testid="playlist-importer">
      <div className="bg-indigo-900/20 border border-indigo-700/30 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <ListMusic className="w-4 h-4 text-indigo-300 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-indigo-200">
            <p className="font-semibold mb-1">Importar playlist completa</p>
            <ul className="list-disc list-inside space-y-1 text-indigo-300/90">
              <li>Pega URL de playlist de YouTube o Spotify</li>
              <li>Spotify se resuelve automáticamente a YouTube y descarga MP3</li>
              <li>Hasta 100 canciones por importación</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <Label htmlFor="pl-url" className="text-slate-200">
            URL de playlist
          </Label>
          <div className="flex gap-2">
            <Input
              id="pl-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://open.spotify.com/playlist/... o https://www.youtube.com/playlist?list=..."
              className="bg-slate-700 border-slate-600 text-white"
              disabled={loadingPreview || importing}
              data-testid="playlist-url-input"
            />
            <Button
              type="button"
              onClick={handlePreview}
              disabled={loadingPreview || importing || !url.trim()}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="playlist-preview-btn"
            >
              {loadingPreview ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
            </Button>
          </div>
          {detectedKind && (
            <p className="text-xs text-slate-500 mt-1">
              Detectado: {detectedKind === 'spotify_playlist' ? 'Playlist Spotify' : 'YouTube'}
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="pl-nota" className="text-slate-200">
            Nota (opcional)
          </Label>
          <Input
            id="pl-nota"
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            placeholder="Ej: Lista de la novia, Playlist cliente..."
            className="bg-slate-700 border-slate-600 text-white"
            disabled={importing}
            data-testid="playlist-import-nota-input"
          />
          <p className="text-xs text-slate-500 mt-1">
            Esta nota aparecerá junto a cada canción importada en la lista de peticiones
          </p>
        </div>

        <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2 text-slate-300 cursor-pointer">
            <input
              type="checkbox"
              checked={autoDownload}
              onChange={(e) => setAutoDownload(e.target.checked)}
              disabled={importing}
            />
            Descargar MP3 automáticamente
          </label>
          <label className="flex items-center gap-2 text-slate-300 cursor-pointer">
            <input
              type="checkbox"
              checked={autoResolveSpotify}
              onChange={(e) => setAutoResolveSpotify(e.target.checked)}
              disabled={importing}
            />
            Resolver Spotify→YouTube
          </label>
        </div>
      </div>

      {tracks.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-400">
              {selected.size} de {tracks.length} seleccionadas — fuente: {source}
            </p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={toggleAll}
              className="text-slate-300 hover:text-white"
              data-testid="toggle-all-btn"
            >
              {allSelected ? <CheckSquare className="w-4 h-4 mr-1" /> : <Square className="w-4 h-4 mr-1" />}
              {allSelected ? 'Deseleccionar todo' : 'Seleccionar todo'}
            </Button>
          </div>
          <div
            className="max-h-72 overflow-y-auto border border-slate-700 rounded-lg divide-y divide-slate-800"
            data-testid="playlist-tracks-list"
          >
            {tracks.map((t, idx) => (
              <label
                key={t.youtube_url || t.spotify_id || `${t.title}-${idx}`}
                className={`flex items-center gap-3 p-2 cursor-pointer transition-colors ${
                  selected.has(idx) ? 'bg-indigo-900/30' : 'hover:bg-slate-800/40'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(idx)}
                  onChange={() => toggle(idx)}
                  className="w-4 h-4 flex-shrink-0"
                />
                {t.thumbnail ? (
                  <img
                    src={t.thumbnail}
                    alt={t.title || 'cover'}
                    className="w-10 h-10 rounded object-cover flex-shrink-0"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-10 h-10 rounded bg-slate-700 flex items-center justify-center flex-shrink-0">
                    <Music2 className="w-4 h-4 text-slate-500" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm truncate">{t.title || '(Sin título)'}</div>
                  <div className="text-slate-400 text-xs truncate">{t.artist}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 justify-end">
        <Button
          onClick={onClose}
          variant="outline"
          disabled={importing}
          className="border-slate-600 text-slate-300 hover:bg-slate-700"
        >
          Cancelar
        </Button>
        <Button
          onClick={handleImport}
          disabled={importing || tracks.length === 0 || selected.size === 0}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="playlist-import-btn"
        >
          {importing ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Importando…
            </>
          ) : (
            <>
              <Download className="w-4 h-4 mr-2" />
              Importar {selected.size}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default PlaylistImporter;
